import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { PublicSidebarSection } from "@/components/layout/PublicSidebar";

interface SidebarActiveContextValue {
  overrideActive: PublicSidebarSection | null;
  setOverrideActive: (section: PublicSidebarSection | null) => void;
  // Shared open state for the account sheet (screen 32c) so the header
  // avatar, the mobile bottom bar's "You" tab and the menu tray's account
  // row all drive the same sheet instance instead of each mounting their own.
  accountSheetOpen: boolean;
  setAccountSheetOpen: (open: boolean) => void;
  // Set by a page (the compact artist/track feed) that replaces
  // PublicMobileHeader with its own full-bleed content and back control,
  // so Layout hides the header instead of it floating above that page.
  hideMobileHeader: boolean;
  setHideMobileHeader: (hidden: boolean) => void;
}

const SidebarActiveContext = createContext<SidebarActiveContextValue | null>(
  null,
);

export function SidebarActiveProvider({ children }: { children: ReactNode }) {
  const [overrideActive, setOverrideActive] =
    useState<PublicSidebarSection | null>(null);
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  const [hideMobileHeader, setHideMobileHeader] = useState(false);

  return (
    <SidebarActiveContext.Provider
      value={{
        overrideActive,
        setOverrideActive,
        accountSheetOpen,
        setAccountSheetOpen,
        hideMobileHeader,
        setHideMobileHeader,
      }}
    >
      {children}
    </SidebarActiveContext.Provider>
  );
}

export function useSidebarActiveContext() {
  const ctx = useContext(SidebarActiveContext);
  if (!ctx) {
    throw new Error(
      "useSidebarActiveContext must be used within a SidebarActiveProvider",
    );
  }
  return ctx;
}

// Lets a page whose sidebar highlight depends on data it loads (e.g. a
// profile page that only knows "artist" vs "sponsor" once the profile has
// fetched) override the section Layout would otherwise guess from the URL
// alone. Clears itself on unmount so the next route falls back to the
// URL-based default.
export function useSidebarActiveOverride(section: PublicSidebarSection | null) {
  const { setOverrideActive } = useSidebarActiveContext();
  useEffect(() => {
    setOverrideActive(section);
    return () => setOverrideActive(null);
  }, [section, setOverrideActive]);
}

// Lets a full-bleed page (the compact artist/track feed) hide
// PublicMobileHeader for as long as it's mounted, restoring it the instant
// the visitor navigates away.
export function useHideMobileHeader(hidden: boolean) {
  const { setHideMobileHeader } = useSidebarActiveContext();
  useEffect(() => {
    setHideMobileHeader(hidden);
    return () => setHideMobileHeader(false);
  }, [hidden, setHideMobileHeader]);
}
